import {
  Badge,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserAvatar,
  cn,
} from "@nexus/ui";
import {
  TASK_NAVIGATION_SECTION,
  type PersonaHookVariationSuggestion,
} from "@nexus/contracts";
import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsActiveSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { humanizeConceptLabel } from "@/features/personas/lib/persona-format";
import { resolveOriginalBriefUrl } from "@/features/opportunities/lib/original-brief-url";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { useRevealTarget } from "@/hooks/useRevealTarget";
import { getRevealHref, navigateAndReveal } from "@/lib/navigation/navigate-and-reveal";
import {
  jumpToTaskTargetWhenReady,
  readTaskItem,
  readTaskOpportunityId,
  readTaskSection,
  TASK_SECTION_SEARCH_PARAM,
} from "@/features/task-progress/lib/task-section";
import { isScriptSentInMap, isSentToAnyPartner } from "@/features/marketplace/lib/sent-scripts-map";
import type { SentScriptsMap } from "@/features/marketplace/types/marketplace.types";
import { HighlightText } from "@/features/opportunities/components/HighlightText";
import { ScriptPlatformBadge } from "@/features/persona-opportunities/components/platform/ScriptPlatformBadge";
import type { PersonaBriefDto } from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import { matchesScriptPlatform } from "@/features/persona-opportunities/lib/script-platform";
import type { PersonaOpportunitySetDetail, PersonaOpportunitySetType } from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import type {
  PersonaScriptContentPatch,
  PersonaScriptDto,
} from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import {
  canShowAddThreeHooks,
  shouldOpenHookVariationsPanel,
} from "@/features/persona-opportunities/lib/hook-variations-eligibility";
import {
  getHookVariantDisplayMeta,
  isPersonaScriptHookVariant,
  sortPersonaScriptsWithVariantsAfterParent,
} from "@/features/persona-opportunities/lib/hook-variations-order";
import { formatPersonaBriefForCopy } from "@/features/persona-opportunities/lib/persona-brief-clipboard";
import {
  downloadPersonaSelection,
  personaSelectionFilename,
  scriptDocxLabels,
  type PersonaDocxPart,
  type PersonaDocumentExportFormat,
} from "@/features/persona-opportunities/lib/persona-script-docx";
import { formatScriptCreatedAt } from "@/features/persona-opportunities/lib/persona-script-meta";
import { isPersonaScriptsGenerationActiveForSet } from "@/features/persona-opportunities/lib/persona-scripts-generation-active";
import { estimateBroadcastDescriptionLength } from "@/features/persona-opportunities/lib/scripts-broadcast-guideline-rules";
import {
  DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS,
  PERSONA_SCRIPTS_LIST_SEARCH_KEYS,
  personaScriptsListSearchSnapshot,
  readPersonaScriptsListSearch,
  toPersonaScriptsListSearchParams,
  type PersonaScriptsListFilters,
} from "@/features/persona-opportunities/lib/persona-scripts-list-search";
import { PERSONA_STAGE_RESULTS_CLASS } from "@/features/persona-opportunities/lib/persona-opportunity-stage-chrome";
import { getPersonaScriptDisplayTitle } from "@/features/persona-opportunities/lib/script-display-title";
import {
  PERSONA_SCRIPTS_LIVE_REFETCH_MS,
  activeTasksQueryOptions,
  personaOpportunitySetBriefsQueryOptions,
  personaOpportunitySetKeys,
  personaOpportunitySetScriptsQueryOptions,
  sentScriptsQueryOptions,
  useAiEditPersonaBriefs,
  useAiEditPersonaScripts,
  useConfirmPersonaHookVariations,
  useDeletePersonaBriefs,
  useDeletePersonaScripts,
  usePatchPersonaBriefContent,
  usePatchPersonaScriptContent,
  useSuggestPersonaHookVariations,
  useTogglePersonaBriefFavorite,
  useTogglePersonaScriptFavorite,
} from "@/lib/queries";

import { PersonaGenerateScriptModal } from "../../generate-script/PersonaGenerateScriptModal.js";
import { PersonaOpportunitySelectCheckbox } from "../../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import { PersonaOpportunityTabPlaceholder } from "../PersonaOpportunityTabPlaceholder/PersonaOpportunityTabPlaceholder.js";
import { ScriptCustomAssetRowLink } from "@/features/marketplace/components/ScriptCustomAssetRowLink";
import { ScriptBroadcastSource } from "@/features/persona-opportunities/components/shared/ScriptBroadcastSource/ScriptBroadcastSource";
import { isCustomAssetScript } from "@/features/marketplace/lib/custom-asset";

import { BroadcastScriptsModal, type BroadcastScript } from "./BroadcastScriptsModal.js";
import { BriefsHistoryDrawer } from "./BriefsHistoryDrawer.js";
import { DeletePersonaScriptsDialog } from "./DeletePersonaScriptsDialog.js";
import { PersonaAiBriefEditorModal } from "./PersonaAiBriefEditorModal.js";
import { PersonaAiScriptEditorModal } from "./PersonaAiScriptEditorModal.js";
import { parsePersonaBriefAiCell, PersonaBriefCard } from "./PersonaBriefCard.js";
import { PersonaScriptBulkActionBar } from "./PersonaScriptBulkActionBar.js";
import { PERSONA_SCRIPT_CELL_FLASH_MS } from "./PersonaScriptEditableCell.js";
import { PersonaScriptExpandedBody } from "./PersonaScriptExpandedBody.js";
import { parsePersonaScriptSceneCellKey } from "./PersonaScriptSceneTable.js";
import { PersonaScriptTitleEditor } from "./PersonaScriptTitleEditor.js";
import { PersonaScriptsToolbar } from "./PersonaScriptsToolbar.js";
import { PersonaSendToVendorDialog } from "./PersonaSendToVendorDialog/PersonaSendToVendorDialog.js";
import { ScriptsHistoryDrawer } from "./ScriptsHistoryDrawer.js";
import {
  personaBriefReferenceDetails,
  personaScriptReferenceDetails,
  useBriefReferenceLabels,
} from "@/features/persona-opportunities/lib/persona-script-reference-details";

export interface ScriptsTabProps {
  detail?: PersonaOpportunitySetDetail;
  isDetailLoading?: boolean;
  canGenerateScripts?: boolean;
  /** Inline edit, AI edit, delete, restore, and extra hooks. Defaults to canGenerateScripts. */
  canEditScripts?: boolean;
  importOpen?: boolean;
  onImportOpenChange?: (open: boolean) => void;
  onGoToOpportunities?: () => void;
  setType?: PersonaOpportunitySetType;
}

interface ScriptGroup {
  opportunityId: string;
  angle: string;
  personaTitle: string;
  drivenFeature: string | null;
  isCustom: boolean;
  scripts: PersonaScriptDto[];
  briefs: PersonaBriefDto[];
}

type DeleteJobResult = {
  kind: "scripts" | "briefs";
  deletedIds: string[];
};

function drivenFeatureLabel(
  opportunity: {
    drivenBy: "painpoint" | "motivation" | null;
    painPoint: string | null;
    motivation: string | null;
  },
): string | null {
  if (opportunity.drivenBy === "painpoint") return opportunity.painPoint?.trim() || null;
  if (opportunity.drivenBy === "motivation") return opportunity.motivation?.trim() || null;
  return opportunity.painPoint?.trim() || opportunity.motivation?.trim() || null;
}

function uniqueScriptCreators(
  scripts: PersonaScriptDto[],
): NonNullable<PersonaScriptDto["createdBy"]>[] {
  const seen = new Set<string>();
  const creators: NonNullable<PersonaScriptDto["createdBy"]>[] = [];
  for (const script of scripts) {
    const creator = script.createdBy;
    if (!creator) continue;
    const key = `${creator.displayName}\0${creator.email ?? ""}\0${creator.photoUrl ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    creators.push(creator);
  }
  return creators;
}

function customGroupCreatedByLabel(
  creators: NonNullable<PersonaScriptDto["createdBy"]>[],
  t: TFunction,
): string | null {
  if (creators.length === 0) return null;
  const name = creators[0]!.displayName;
  if (creators.length === 1) {
    return t("personaOpportunities.scripts.customGroupCreatedBy", {
      defaultValue: "Created by {{name}}",
      name,
    });
  }
  return t("personaOpportunities.scripts.customGroupCreatedByOthers", {
    defaultValue_one: "Created by {{name}} and {{count}} other",
    defaultValue_other: "Created by {{name}} and {{count}} others",
    name,
    count: creators.length - 1,
  });
}

function newestScriptIdInGroup(
  groups: ScriptGroup[],
  opportunityId: string,
): string | null {
  const group = groups.find((entry) => entry.opportunityId === opportunityId);
  if (!group || group.scripts.length === 0) return null;
  let newest = group.scripts[0]!;
  for (const script of group.scripts) {
    if (script.createdAt > newest.createdAt) newest = script;
  }
  return newest.id;
}

function scriptMetaLine(
  script: PersonaScriptDto,
  t: TFunction,
  variantMeta?: { parentSlot: number; ordinal: number } | null,
): string {
  const slotLabel = variantMeta
    ? t("personaOpportunities.scripts.variantSlotLabel", {
        defaultValue: "S{{parent}}·H{{ordinal}}",
        parent: variantMeta.parentSlot,
        ordinal: variantMeta.ordinal,
      })
    : t("personaOpportunities.scripts.slotLabel", {
        defaultValue: "S{{slot}}",
        slot: script.slotIndex,
      });
  const parts: string[] = [
    slotLabel,
    t("personaOpportunities.scripts.sceneCount", {
      defaultValue: "{{count}} scenes",
      count: script.scenes.length,
    }),
  ];
  const duration = script.duration?.trim();
  if (duration) parts.push(duration);
  return parts.join(" · ");
}

function matchesSentStatus(
  id: string,
  sentStatus: PersonaScriptsListFilters["sentStatus"],
  sentScriptsMap: SentScriptsMap | undefined,
): boolean {
  if (sentScriptsMap == null) return true;
  const isSent = isScriptSentInMap(sentScriptsMap, id);
  if (sentStatus === "sent") return isSent;
  if (sentStatus === "notSent") return !isSent;
  return true;
}

function briefMatchesLenses(
  brief: PersonaBriefDto,
  group: ScriptGroup,
  search: string,
  filters: PersonaScriptsListFilters,
  sentScriptsMap: SentScriptsMap | undefined,
): boolean {
  if (!briefMatchesSearch(brief, group, search)) return false;
  if (filters.favoritesOnly && !brief.isFavorite) return false;
  if (filters.hasSketch === "yes") return false;
  if (filters.hasAnimatic === "yes") return false;
  if (!matchesScriptPlatform(brief.youtubeFormat, filters.platform)) return false;
  return matchesSentStatus(brief.id, filters.sentStatus, sentScriptsMap);
}

function briefMatchesSearch(brief: PersonaBriefDto, group: ScriptGroup, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    brief.videoName,
    brief.duration,
    brief.concept.direction,
    brief.concept.line,
    brief.cta.direction,
    brief.cta.line,
    brief.notes,
    brief.creatorProfile.ageRange,
    brief.creatorProfile.gender,
    brief.creatorProfile.preferences,
    ...brief.hooks.flatMap((hook) => [hook.direction, hook.line]),
    group.angle,
    group.personaTitle,
    group.drivenFeature,
  ]
    .filter((value): value is string => !!value?.trim())
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

function hasSuccessfulBriefEditResult(result: unknown, briefId: string): boolean {
  if (!result || typeof result !== "object") return false;
  const results = (result as { results?: unknown }).results;
  if (!Array.isArray(results)) return false;

  return results.some((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const row = entry as { briefId?: unknown; status?: unknown };
    return row.briefId === briefId && row.status === "success";
  });
}

function removeDeletedIds(selectedIds: Set<string>, deletedIds: string[]): Set<string> {
  if (deletedIds.length === 0) return selectedIds;
  const next = new Set(selectedIds);
  for (const id of deletedIds) next.delete(id);
  return next;
}

function groupCountLabel(group: ScriptGroup, t: TFunction): string {
  if (group.briefs.length === 0) {
    return t("personaOpportunities.scripts.groupCount", {
      defaultValue_one: "{{count}} script",
      defaultValue_other: "{{count}} scripts",
      count: group.scripts.length,
    });
  }
  if (group.scripts.length === 0) {
    return t("personaOpportunities.scripts.briefs.groupCount", {
      defaultValue_one: "{{count}} brief",
      defaultValue_other: "{{count}} briefs",
      count: group.briefs.length,
    });
  }
  return t("personaOpportunities.scripts.groupCountMixed", {
    defaultValue: "{{scripts}} · {{briefs}}",
    scripts: t("personaOpportunities.scripts.groupCount", {
      defaultValue_one: "{{count}} script",
      defaultValue_other: "{{count}} scripts",
      count: group.scripts.length,
    }),
    briefs: t("personaOpportunities.scripts.briefs.groupCount", {
      defaultValue_one: "{{count}} brief",
      defaultValue_other: "{{count}} briefs",
      count: group.briefs.length,
    }),
  });
}

function scriptMatchesSearch(script: PersonaScriptDto, group: ScriptGroup, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    script.hook,
    script.concept,
    script.concept ? humanizeConceptLabel(script.concept) : null,
    script.adDescription,
    script.hookStrategy,
    group.angle,
    group.personaTitle,
    group.drivenFeature,
  ]
    .filter((value): value is string => !!value?.trim())
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

function hasSketchStoryboard(script: PersonaScriptDto): boolean {
  return script.storyboardStatus === "READY";
}

function hasAnimaticStoryboard(script: PersonaScriptDto): boolean {
  return script.storyboardPanelStatus === "READY";
}

function scriptMatchesLenses(
  script: PersonaScriptDto,
  group: ScriptGroup,
  search: string,
  filters: PersonaScriptsListFilters,
  sentScriptsMap: SentScriptsMap | undefined,
): boolean {
  if (!scriptMatchesSearch(script, group, search)) return false;
  if (filters.favoritesOnly && !script.isFavorite) return false;
  if (filters.hasSketch === "yes" && !hasSketchStoryboard(script)) return false;
  if (filters.hasAnimatic === "yes" && !hasAnimaticStoryboard(script)) return false;
  if (!matchesScriptPlatform(script.youtubeFormat, filters.platform)) return false;
  return matchesSentStatus(script.id, filters.sentStatus, sentScriptsMap);
}

const SEARCH_DEBOUNCE_MS = 300;

export function ScriptsTab({
  detail,
  isDetailLoading = false,
  canGenerateScripts = false,
  canEditScripts = canGenerateScripts,
  importOpen = false,
  onImportOpenChange,
  onGoToOpportunities,
  setType,
}: ScriptsTabProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const userEmail = useAuth((s) => s.user?.email);
  const isActiveSuperTenant = useIsActiveSuperTenant();
  const perms = usePermissions();
  // The role on the active tenant must hold the grant; no super-tenant bypass (AD-3319).
  const canBroadcast = isActiveSuperTenant && perms.SCRIPTS_BROADCAST.isOn;
  const searchParams = useSearch({ strict: false }) as Record<string, unknown> & {
    storyboard?: string;
    scriptId?: string;
    briefId?: string;
    opportunityId?: string;
    variant?: "sketch" | "panels";
    highlight?: string;
    section?: string;
  };
  const {
    storyboard: expandedStoryboardScriptId,
    scriptId: searchScriptId,
    briefId: searchBriefId,
    variant: searchVariant,
  } = searchParams;
  const urlListSearch = useMemo(
    () => readPersonaScriptsListSearch(searchParams),
    [
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites],
      searchParams[PERSONA_SCRIPTS_LIST_SEARCH_KEYS.platform],
    ],
  );
  const urlListSearchSnapshot = personaScriptsListSearchSnapshot(
    urlListSearch.search,
    urlListSearch.filters,
  );
  const taskSection = readTaskSection(searchParams);
  const taskItemId = readTaskItem(searchParams);
  const taskOpportunityId = readTaskOpportunityId(searchParams);
  const isStoryboardDeepLink =
    searchVariant === "sketch" || searchVariant === "panels";
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedScripts, setExpandedScripts] = useState<Record<string, boolean>>({});
  const [expandedBriefs, setExpandedBriefs] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState(urlListSearch.search);
  const [filters, setFilters] = useState<PersonaScriptsListFilters>(urlListSearch.filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const urlSnapshotRef = useRef(urlListSearchSnapshot);
  const writeListSearchParams = useCallback(
    (
      nextSearch: string,
      nextFilters: PersonaScriptsListFilters,
      dropConsumedTaskJumpParams = false,
    ) => {
      void navigate({
        to: ".",
        search: (previous) => ({
          ...previous,
          ...toPersonaScriptsListSearchParams(nextSearch, nextFilters),
          ...(dropConsumedTaskJumpParams
            ? {
                [TASK_SECTION_SEARCH_PARAM]: undefined,
                opportunityId: undefined,
                variant: undefined,
              }
            : {}),
        }),
        replace: true,
      });
    },
    [navigate],
  );
  const { debounced: debouncedWriteSearch, cancel: cancelSearchWrite } = useDebouncedCallback(
    (value: string) => {
      writeListSearchParams(value, filtersRef.current, true);
    },
    SEARCH_DEBOUNCE_MS,
  );
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (value.trim() === "") {
        cancelSearchWrite();
        writeListSearchParams("", filtersRef.current, true);
        return;
      }
      debouncedWriteSearch(value);
    },
    [cancelSearchWrite, debouncedWriteSearch, writeListSearchParams],
  );
  const handleFilterChange = useCallback(
    <K extends keyof PersonaScriptsListFilters>(
      key: K,
      value: PersonaScriptsListFilters[K],
    ) => {
      const next = { ...filtersRef.current, [key]: value };
      filtersRef.current = next;
      setFilters(next);
      cancelSearchWrite();
      writeListSearchParams(search, next, true);
    },
    [cancelSearchWrite, search, writeListSearchParams],
  );
  const [selectedScriptIds, setSelectedScriptIds] = useState<Set<string>>(() => new Set());
  const [selectedBriefIds, setSelectedBriefIds] = useState<Set<string>>(() => new Set());
  const [isExporting, setIsExporting] = useState(false);
  const discardedCorrelationIds = useRef(new Set<string>());
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [flash, setFlash] = useState<{
    scriptId: string;
    key: string;
    source: "manual" | "ai";
  } | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [aiEditKey, setAiEditKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [hookPanel, setHookPanel] = useState<{
    correlationId: string;
    parentScriptId: string;
    variants: PersonaHookVariationSuggestion[];
  } | null>(null);

  const suggestHooks = useSuggestPersonaHookVariations({
    onCompleted: (completed) => {
      if (!shouldOpenHookVariationsPanel(completed.correlationId, discardedCorrelationIds.current)) {
        return;
      }
      setHookPanel(completed);
    },
  });
  const confirmHooks = useConfirmPersonaHookVariations();
  const patchContent = usePatchPersonaScriptContent();
  const patchBriefContent = usePatchPersonaBriefContent();
  const toggleFavorite = useTogglePersonaScriptFavorite();
  const toggleBriefFavorite = useTogglePersonaBriefFavorite();
  const deleteScripts = useDeletePersonaScripts();
  const deleteBriefs = useDeletePersonaBriefs();
  const aiEdit = useAiEditPersonaScripts();
  const aiEditBriefs = useAiEditPersonaBriefs();
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [aiEditScripts, setAiEditScripts] = useState<PersonaScriptDto[]>([]);
  const [aiEditBriefsList, setAiEditBriefsList] = useState<PersonaBriefDto[]>([]);
  const [historyScriptId, setHistoryScriptId] = useState<string | null>(null);
  const [historyBriefId, setHistoryBriefId] = useState<string | null>(null);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  function flashCell(scriptId: string, key: string, source: "manual" | "ai" = "manual") {
    setFlash({ scriptId, key, source });
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), PERSONA_SCRIPT_CELL_FLASH_MS);
  }

  function startScriptEditor(scriptId: string, key: string) {
    setAiEditKey(null);
    setAiEditBriefsList([]);
    setEditingKey(`${scriptId}:${key}`);
  }

  function startScriptAiEditor(scriptId: string, key: string) {
    setEditingKey(null);
    setAiEditScripts([]);
    setAiEditBriefsList([]);
    setAiEditKey(`${scriptId}:${key}`);
  }

  function startBriefAiEditor(briefId: string, key: string) {
    setEditingKey(null);
    setAiEditScripts([]);
    setAiEditBriefsList([]);
    setAiEditKey(`${briefId}:${key}`);
  }

  function applyCellAi(scriptId: string, key: string, instruction: string) {
    if (!detail || !activeTenantId) return;
    const cell = parsePersonaScriptSceneCellKey(key);
    if (!cell) return;
    aiEdit.mutate(
      {
        setId: detail.set.id,
        scriptIds: [scriptId],
        instruction,
        tenantId: activeTenantId,
        cell,
      },
      {
        onSuccess: () => flashCell(scriptId, key, "ai"),
        onSettled: () =>
          setAiEditKey((current) => (current === `${scriptId}:${key}` ? null : current)),
      },
    );
  }

  function applyBriefCellAi(briefId: string, key: string, instruction: string) {
    if (!detail || !activeTenantId) return;
    const cell = parsePersonaBriefAiCell(key);
    if (!cell) return;
    aiEditBriefs.mutate(
      {
        setId: detail.set.id,
        briefIds: [briefId],
        instruction,
        tenantId: activeTenantId,
        idempotencyKey: crypto.randomUUID(),
        cell,
      },
      {
        onSuccess: (result) => {
          if (hasSuccessfulBriefEditResult(result, briefId)) {
            flashCell(briefId, key, "ai");
          }
        },
        onSettled: () =>
          setAiEditKey((current) => (current === `${briefId}:${key}` ? null : current)),
      },
    );
  }

  function saveScriptContent(
    scriptId: string,
    key: string,
    patch: PersonaScriptContentPatch,
  ) {
    if (!detail || !activeTenantId) return;
    setSavingKey(`${scriptId}:${key}`);
    patchContent.mutate(
      { setId: detail.set.id, scriptId, tenantId: activeTenantId, patch },
      {
        onSuccess: () => flashCell(scriptId, key),
        onSettled: () => setSavingKey(null),
      },
    );
  }
  const [sendScriptIds, setSendScriptIds] = useState<string[]>([]);
  const [sendBriefIds, setSendBriefIds] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [broadcastScripts, setBroadcastScripts] = useState<BroadcastScript[]>([]);
  const sentScriptsQuery = useQuery(sentScriptsQueryOptions(activeTenantId));
  const sentScriptsMap = sentScriptsQuery.data;
  const isSentMapReady = sentScriptsMap != null;
  const openOrder = useCallback(
    (
      orderId: string,
      _orderScriptId: string | undefined,
      event: ReactMouseEvent<HTMLAnchorElement>,
    ) => {
      void navigateAndReveal({
        route: "/persona-opportunities",
        targetId: orderId,
        expandPath: [orderId],
        search: {
          setId: detail?.set.id,
          tab: "sent_to_vendor",
          orderId,
        },
        event,
        navigate: () =>
          navigate({
            to: "/persona-opportunities",
            search: (previous) => ({
              ...previous,
              setId: detail?.set.id,
              tab: "sent_to_vendor",
              orderId,
            }),
          }),
      });
    },
    [detail?.set.id, navigate],
  );

  const { data: activeTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));
  const isGeneratingScripts = isPersonaScriptsGenerationActiveForSet(
    activeTasks,
    detail?.set.id,
  );
  const scriptsRefetchInterval = isGeneratingScripts ? PERSONA_SCRIPTS_LIVE_REFETCH_MS : false;

  const opportunityRefs = useMemo(() => {
    if (!detail) return [];
    return detail.personas.flatMap((persona) =>
      persona.opportunities.map((opportunity) => ({
        opportunityId: opportunity.id,
        angle: opportunity.angle,
        personaTitle: persona.title,
        drivenFeature: drivenFeatureLabel(opportunity),
        isCustom: opportunity.isCustom === true,
      })),
    );
  }, [detail]);

  const opportunityMetaById = useMemo(() => {
    const map = new Map<
      string,
      { angle: string; personaTitle: string; drivenFeature: string | null; isCustom: boolean }
    >();
    for (const ref of opportunityRefs) {
      map.set(ref.opportunityId, {
        angle: ref.angle,
        personaTitle: ref.personaTitle,
        drivenFeature: ref.drivenFeature,
        isCustom: ref.isCustom,
      });
    }
    return map;
  }, [opportunityRefs]);

  const shouldHideBriefs = setType === "COMPETITOR";
  const {
    data: scripts = [],
    isPending: isScriptsQueryPending,
    isError: isScriptsQueryError,
  } = useQuery(
    personaOpportunitySetScriptsQueryOptions(activeTenantId, detail?.set.id ?? "", {
      enabled: !!detail?.set.id && !isDetailLoading,
      refetchInterval: scriptsRefetchInterval,
    }),
  );
  const {
    data: briefs = [],
    isPending: isBriefsQueryPending,
    isError: isBriefsQueryError,
  } = useQuery(
    personaOpportunitySetBriefsQueryOptions(activeTenantId, detail?.set.id ?? "", {
      enabled: !!detail?.set.id && !isDetailLoading && !shouldHideBriefs,
      refetchInterval: scriptsRefetchInterval,
    }),
  );
  const listedScripts = scripts;
  const listedBriefs = shouldHideBriefs ? [] : briefs;
  const scriptTitlesById = useMemo(
    () =>
      Object.fromEntries(
        listedScripts.map((script) => [
          script.id,
          getPersonaScriptDisplayTitle(
            script,
            t("personaOpportunities.scripts.untitledHook", {
              defaultValue: "Script {{slot}}",
              slot: script.slotIndex,
            }),
          ),
        ]),
      ),
    [listedScripts, t],
  );
  const scriptHooksById = useMemo(
    () => Object.fromEntries(listedScripts.map((script) => [script.id, script.hooks ?? []])),
    [listedScripts],
  );
  const scriptDetailsById = useMemo(
    () =>
      Object.fromEntries(
        listedScripts.map((script) => [script.id, personaScriptReferenceDetails(script)]),
      ),
    [listedScripts],
  );
  const briefReferenceLabels = useBriefReferenceLabels();
  const briefDetailsById = useMemo(
    () =>
      Object.fromEntries(
        listedBriefs.map((brief) => [
          brief.id,
          personaBriefReferenceDetails(brief, briefReferenceLabels),
        ]),
      ),
    [briefReferenceLabels, listedBriefs],
  );
  const briefTitlesById = useMemo(
    () =>
      Object.fromEntries(
        listedBriefs.map((brief) => [
          brief.id,
          brief.videoName?.trim() ||
            t("personaOpportunities.scripts.briefs.untitled", "Untitled brief"),
        ]),
      ),
    [listedBriefs, t],
  );
  const historyScript = listedScripts.find((script) => script.id === historyScriptId) ?? null;
  const historyBrief = listedBriefs.find((row) => row.id === historyBriefId) ?? null;

  const groups: ScriptGroup[] = useMemo(() => {
    const scriptsByOpportunity = new Map<string, PersonaScriptDto[]>();
    for (const script of listedScripts) {
      const existing = scriptsByOpportunity.get(script.personaOpportunityId) ?? [];
      existing.push(script);
      scriptsByOpportunity.set(script.personaOpportunityId, existing);
    }

    const briefsByOpportunity = new Map<string, PersonaBriefDto[]>();
    for (const brief of listedBriefs) {
      const existing = briefsByOpportunity.get(brief.personaOpportunityId) ?? [];
      existing.push(brief);
      briefsByOpportunity.set(brief.personaOpportunityId, existing);
    }

    const opportunityIdsInGroups = new Set([
      ...scriptsByOpportunity.keys(),
      ...briefsByOpportunity.keys(),
    ]);

    const grouped: ScriptGroup[] = [];
    for (const opportunityId of opportunityIdsInGroups) {
      const meta = opportunityMetaById.get(opportunityId);
      grouped.push({
        opportunityId,
        angle: meta?.angle ?? opportunityId,
        personaTitle: meta?.personaTitle ?? "",
        drivenFeature: meta?.drivenFeature ?? null,
        isCustom: meta?.isCustom === true,
        scripts: sortPersonaScriptsWithVariantsAfterParent(
          scriptsByOpportunity.get(opportunityId) ?? [],
        ),
        briefs: (briefsByOpportunity.get(opportunityId) ?? [])
          .slice()
          .sort((a, b) => a.slotIndex - b.slotIndex),
      });
    }

    const order = new Map(opportunityRefs.map((ref, index) => [ref.opportunityId, index]));
    grouped.sort((a, b) => {
      if (a.isCustom !== b.isCustom) return a.isCustom ? -1 : 1;
      return (
        (order.get(a.opportunityId) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b.opportunityId) ?? Number.MAX_SAFE_INTEGER)
      );
    });
    return grouped;
  }, [listedBriefs, listedScripts, opportunityMetaById, opportunityRefs]);

  const opportunityJumpScriptId = useMemo(() => {
    if (taskItemId || !taskOpportunityId) return null;
    return newestScriptIdInGroup(groups, taskOpportunityId);
  }, [groups, taskItemId, taskOpportunityId]);
  const highlightItemId =
    expandedStoryboardScriptId ??
    searchScriptId ??
    searchBriefId ??
    taskItemId ??
    opportunityJumpScriptId ??
    undefined;
  const jumpScriptId = taskItemId ?? opportunityJumpScriptId;
  const shouldJumpToScriptRow =
    !isStoryboardDeepLink &&
    !!jumpScriptId &&
    (taskSection === TASK_NAVIGATION_SECTION.PERSONA_SCRIPTS || !!taskOpportunityId);
  // Storyboard jumps skip shouldJumpToScriptRow (ScriptStoryboard owns scroll).
  // The row still has to exist in visibleGroups, so clear lenses for that path too.
  const hasTaskJumpLock =
    isStoryboardDeepLink ||
    !!searchScriptId ||
    !!searchBriefId ||
    taskSection === TASK_NAVIGATION_SECTION.PERSONA_SCRIPTS ||
    !!taskOpportunityId;
  /**
   * Jump settle is supposed to drop `?section=` / `?opportunityId=`, but those
   * params (and leftover `highlight` / `scriptId` / `briefId`) often outlive the
   * reveal. Pinning filters to defaults for as long as the lock is in the URL
   * makes a later Favorites/kind pick appear to do nothing. One-shot per target,
   * same pattern as OpportunitiesTab. Clearing the ref when the lock leaves lets
   * a later card click that re-adds `?section=` reveal again.
   */
  const taskTargetKey = `${isStoryboardDeepLink ? (searchVariant ?? "") : ""}|${taskSection ?? ""}|${taskOpportunityId ?? ""}|${taskItemId ?? ""}|${searchScriptId ?? ""}|${searchBriefId ?? ""}`;
  const revealedTargetRef = useRef<string | null>(null);
  const shouldRevealTaskTarget =
    hasTaskJumpLock && revealedTargetRef.current !== taskTargetKey;
  const listSearch = shouldRevealTaskTarget ? "" : search;
  const listFilters = shouldRevealTaskTarget
    ? DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS
    : filters;

  useEffect(() => {
    if (shouldRevealTaskTarget) {
      urlSnapshotRef.current = urlListSearchSnapshot;
      cancelSearchWrite();
      return;
    }
    if (urlSnapshotRef.current === urlListSearchSnapshot) return;
    urlSnapshotRef.current = urlListSearchSnapshot;
    cancelSearchWrite();
    setSearch(urlListSearch.search);
    setFilters(urlListSearch.filters);
    filtersRef.current = urlListSearch.filters;
  }, [shouldRevealTaskTarget, urlListSearch, urlListSearchSnapshot, cancelSearchWrite]);

  useEffect(() => {
    if (!highlightItemId) return;
    const group = groups.find(
      (entry) =>
        entry.scripts.some((script) => script.id === highlightItemId) ||
        entry.briefs.some((brief) => brief.id === highlightItemId),
    );
    if (!group) return;
    setExpandedGroups((prev) => ({ ...prev, [group.opportunityId]: true }));
    if (group.scripts.some((script) => script.id === highlightItemId)) {
      setExpandedScripts((prev) => ({ ...prev, [highlightItemId]: true }));
    }
    if (group.briefs.some((brief) => brief.id === highlightItemId)) {
      setExpandedBriefs((prev) => ({ ...prev, [highlightItemId]: true }));
    }
  }, [groups, highlightItemId]);

  const visibleGroups: ScriptGroup[] = useMemo(() => {
    const documentKind =
      shouldHideBriefs && listFilters.documentKind === "briefs" ? "both" : listFilters.documentKind;
    return groups
      .map((group) => ({
        ...group,
        scripts:
          documentKind === "briefs"
            ? []
            : group.scripts.filter((script) =>
                scriptMatchesLenses(script, group, listSearch, listFilters, sentScriptsMap),
              ),
        briefs:
          shouldHideBriefs || documentKind === "scripts"
            ? []
            : group.briefs.filter((brief) =>
                briefMatchesLenses(brief, group, listSearch, listFilters, sentScriptsMap),
              ),
      }))
      .filter((group) => group.scripts.length > 0 || group.briefs.length > 0);
  }, [shouldHideBriefs, listFilters, groups, listSearch, sentScriptsMap]);

  // Sole owner of non-storyboard script-row scroll (hook variations, AI edit,
  // cell edit, generate-scripts). Keyed on `?section=` so a later card click
  // that only re-adds section re-runs the jump while scriptId stays for expand.
  const targetIsVisible = useMemo(
    () =>
      !!jumpScriptId &&
      visibleGroups.some(
        (entry) =>
          entry.scripts.some((script) => script.id === jumpScriptId) ||
          entry.briefs.some((brief) => brief.id === jumpScriptId),
      ),
    [jumpScriptId, visibleGroups],
  );

  useLayoutEffect(() => {
    if (!hasTaskJumpLock) {
      revealedTargetRef.current = null;
      return;
    }
    if (revealedTargetRef.current === taskTargetKey) return;
    cancelSearchWrite();
    setSearch("");
    setFilters(DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS);
    filtersRef.current = DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS;
    writeListSearchParams("", DEFAULT_PERSONA_SCRIPTS_LIST_FILTERS);
    revealedTargetRef.current = taskTargetKey;
  }, [hasTaskJumpLock, taskTargetKey, cancelSearchWrite, writeListSearchParams]);

  useEffect(() => {
    if (!shouldJumpToScriptRow || !jumpScriptId || !targetIsVisible) return;
    const jump = jumpToTaskTargetWhenReady(
      () => {
        const escaped =
          typeof CSS !== "undefined" && CSS.escape ? CSS.escape(jumpScriptId) : jumpScriptId;
        return document.querySelector<HTMLElement>(`[data-task-item="${escaped}"]`);
      },
      () => {
        void navigate({
          to: ".",
          search: (prev) => ({
            ...prev,
            [TASK_SECTION_SEARCH_PARAM]: undefined,
            opportunityId: undefined,
          }),
          replace: true,
        });
      },
    );
    return jump.cancel;
  }, [shouldJumpToScriptRow, jumpScriptId, targetIsVisible, navigate]);

  const visibleScripts = useMemo(
    () => visibleGroups.flatMap((group) => group.scripts),
    [visibleGroups],
  );
  const visibleBriefs = useMemo(
    () => visibleGroups.flatMap((group) => group.briefs),
    [visibleGroups],
  );
  const visibleScriptIds = useMemo(
    () => visibleScripts.map((script) => script.id),
    [visibleScripts],
  );
  const visibleBriefIds = useMemo(
    () => visibleBriefs.map((brief) => brief.id),
    [visibleBriefs],
  );

  const isLoadingList = isScriptsQueryPending || (!shouldHideBriefs && isBriefsQueryPending);
  const totalScripts = listedScripts.length;
  const totalBriefs = listedBriefs.length;
  const totalItems = totalScripts + totalBriefs;
  const revealId = searchScriptId ?? searchBriefId;
  const revealGroup = revealId
    ? groups.find(
        (group) =>
          group.scripts.some((script) => script.id === revealId) ||
          group.briefs.some((brief) => brief.id === revealId),
      ) ?? null
    : null;
  const revealScriptIndex = revealGroup
    ? revealGroup.scripts.findIndex((script) => script.id === revealId)
    : -1;
  const revealBriefIndex = revealGroup
    ? revealGroup.briefs.findIndex((brief) => brief.id === revealId)
    : -1;
  const { liveAnnouncement: revealAnnouncement } = useRevealTarget({
    targetId: revealId,
    isReady:
      !isLoadingList &&
      (revealGroup
        ? Boolean(
            expandedGroups[revealGroup.opportunityId] &&
              (searchScriptId
                ? expandedScripts[searchScriptId]
                : expandedBriefs[searchBriefId ?? ""]),
          )
        : true),
    getElement: () => {
      if (!revealId) return null;
      return (
        [...document.querySelectorAll<HTMLElement>("[data-task-item]")].find(
          (element) => element.dataset.taskItem === revealId,
        ) ?? null
      );
    },
    announcement: revealGroup
      ? t("personaOpportunities.scripts.revealAnnouncement", {
          defaultValue: "Showing script {{number}} of {{opportunity}}",
          number:
            revealScriptIndex >= 0 ? revealScriptIndex + 1 : revealBriefIndex + 1,
          opportunity: revealGroup.angle,
        })
      : "",
    onReveal: () => {
      void navigate({
        to: ".",
        search: (previous) => ({
          ...previous,
          scriptId: undefined,
          briefId: undefined,
        }),
        replace: true,
      });
    },
    onTimeout: () => {
      toast.error(
        t("personaOpportunities.scripts.revealNotFound", {
          defaultValue: "Couldn't find this script — it may have moved to History",
        }),
        {
          action: {
            label: t("personaOpportunities.scripts.viewOrderHistory", {
              defaultValue: "View History",
            }),
            onClick: () => {
              void navigate({
                to: "/marketplace",
                search: { tab: "history" },
              });
            },
          },
        },
      );
    },
  });
  const hasQueryError =
    !isLoadingList && (isScriptsQueryError || (!shouldHideBriefs && isBriefsQueryError));
  const loadErrorMessage = (() => {
    if (isScriptsQueryError && !shouldHideBriefs && isBriefsQueryError) {
      return t(
        "personaOpportunities.scripts.loadErrorBoth",
        "Could not load scripts and briefs for this set. Try again in a moment.",
      );
    }
    if (!shouldHideBriefs && isBriefsQueryError) {
      return t(
        "personaOpportunities.scripts.loadErrorBriefs",
        "Could not load briefs for this set. Try again in a moment.",
      );
    }
    return t(
      "personaOpportunities.scripts.loadError",
      "Could not load scripts for this set. Try again in a moment.",
    );
  })();

  // Keep set-card / tab badge counts in sync as scripts or briefs land mid-run.
  useEffect(() => {
    if (!isGeneratingScripts || !activeTenantId || !detail?.set.id) return;
    void queryClient.invalidateQueries({
      queryKey: personaOpportunitySetKeys.detail(activeTenantId, detail.set.id),
    });
    void queryClient.invalidateQueries({
      queryKey: personaOpportunitySetKeys.list(activeTenantId),
    });
  }, [activeTenantId, detail?.set.id, isGeneratingScripts, queryClient, totalItems]);

  const allGroupsExpanded =
    visibleGroups.length > 0 &&
    visibleGroups.every((group) => expandedGroups[group.opportunityId] ?? true);

  const handleToggleExpandAll = () => {
    if (allGroupsExpanded) {
      const next: Record<string, boolean> = {};
      for (const group of visibleGroups) next[group.opportunityId] = false;
      setExpandedGroups(next);
      return;
    }
    const next: Record<string, boolean> = {};
    for (const group of visibleGroups) next[group.opportunityId] = true;
    setExpandedGroups(next);
  };

  const visibleSelectableCount = visibleScriptIds.length + visibleBriefIds.length;
  const allVisibleSelected =
    visibleSelectableCount > 0 &&
    visibleScriptIds.every((id) => selectedScriptIds.has(id)) &&
    visibleBriefIds.every((id) => selectedBriefIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected &&
    (visibleScriptIds.some((id) => selectedScriptIds.has(id)) ||
      visibleBriefIds.some((id) => selectedBriefIds.has(id)));

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedScriptIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleScriptIds) next.delete(id);
        return next;
      });
      setSelectedBriefIds((prev) => {
        const next = new Set(prev);
        for (const id of visibleBriefIds) next.delete(id);
        return next;
      });
      return;
    }
    setSelectedScriptIds((prev) => new Set([...prev, ...visibleScriptIds]));
    setSelectedBriefIds((prev) => new Set([...prev, ...visibleBriefIds]));
  };

  const handleToggleSelected = (scriptId: string) => {
    setSelectedScriptIds((prev) => {
      const next = new Set(prev);
      if (next.has(scriptId)) next.delete(scriptId);
      else next.add(scriptId);
      return next;
    });
  };

  const handleToggleBriefSelected = (briefId: string) => {
    setSelectedBriefIds((prev) => {
      const next = new Set(prev);
      if (next.has(briefId)) next.delete(briefId);
      else next.add(briefId);
      return next;
    });
  };

  const handleToggleFavorite = (script: PersonaScriptDto) => {
    if (!detail || !activeTenantId) return;
    toggleFavorite.mutate({
      setId: detail.set.id,
      scriptId: script.id,
      tenantId: activeTenantId,
      isFavorite: !script.isFavorite,
    });
  };

  const handleToggleBriefFavorite = (brief: PersonaBriefDto) => {
    if (!canEditScripts || !detail || !activeTenantId) return;
    toggleBriefFavorite.mutate({
      setId: detail.set.id,
      briefId: brief.id,
      tenantId: activeTenantId,
      isFavorite: !brief.isFavorite,
    });
  };

  const handleToggleScriptExpanded = (scriptId: string) => {
    setExpandedScripts((prev) => ({
      ...prev,
      [scriptId]: !(prev[scriptId] ?? false),
    }));
  };

  const handleExportSelected = (format: PersonaDocumentExportFormat) => {
    if (isExporting) return;
    const untitled = (script: PersonaScriptDto) =>
      t("personaOpportunities.scripts.untitledHook", {
        defaultValue: "Script {{slot}}",
        slot: script.slotIndex,
      });
    const labels = scriptDocxLabels(t);
    const parts: PersonaDocxPart[] = [];
    for (const group of visibleGroups) {
      for (const script of group.scripts) {
        if (selectedScriptIds.has(script.id)) {
          parts.push({ kind: "script", script, untitled: untitled(script), labels });
        }
      }
      for (const brief of group.briefs) {
        if (selectedBriefIds.has(brief.id)) {
          parts.push({ kind: "brief", text: formatPersonaBriefForCopy(brief, t), labels });
        }
      }
    }
    const scriptCount = visibleScripts.filter((script) => selectedScriptIds.has(script.id)).length;
    const briefCount = visibleBriefs.filter((brief) => selectedBriefIds.has(brief.id)).length;
    const filename = personaSelectionFilename(scriptCount, briefCount, format);
    setIsExporting(true);
    void downloadPersonaSelection(parts, format, filename)
      .then(() => {
        const scriptsLabel = t("personaOpportunities.scripts.bulk.exportToast", {
          defaultValue_one: "{{count}} script exported",
          defaultValue_other: "{{count}} scripts exported",
          count: scriptCount,
        });
        const briefsLabel = t("personaOpportunities.scripts.briefs.bulk.exportToast", {
          defaultValue_one: "{{count}} brief exported",
          defaultValue_other: "{{count}} briefs exported",
          count: briefCount,
        });
        toast.success(
          scriptCount > 0 && briefCount > 0
            ? t("personaOpportunities.scripts.bulk.exportToastMixed", {
                defaultValue: "{{scripts}} · {{briefs}}",
                scripts: scriptsLabel,
                briefs: briefsLabel,
              })
            : briefCount > 0
              ? briefsLabel
              : scriptsLabel,
        );
      })
      .catch((error: unknown) => {
        Sentry.captureException(error, { tags: { flow: "persona-script-export" } });
        toast.error(
          t(
            "personaOpportunities.scripts.bulk.exportFailed",
            "Could not export the selected documents",
          ),
        );
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  if (!detail && isDetailLoading) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 px-1 py-8 text-sm text-muted-foreground"
      >
        <Icon name="loader" size="sm" className="animate-spin" aria-hidden />
        {t("personaOpportunities.scripts.loading", "Loading scripts…")}
      </div>
    );
  }

  if (!detail) {
    return (
      <PersonaOpportunityTabPlaceholder
        icon="file-text"
        titleKey="personaOpportunities.tabs.scriptsPlaceholderTitle"
        descriptionKey="personaOpportunities.tabs.scriptsPlaceholderDescription"
        actionLabelKey="personaOpportunities.tabs.goToOpportunities"
        onAction={onGoToOpportunities}
      />
    );
  }

  if (isLoadingList) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 px-1 py-8 text-sm text-muted-foreground"
      >
        <Icon name="loader" size="sm" className="animate-spin" aria-hidden />
        {t("personaOpportunities.scripts.loading", "Loading scripts…")}
      </div>
    );
  }

  if (hasQueryError && totalItems === 0) {
    return (
      <div
        role="alert"
        className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm text-destructive"
      >
        {loadErrorMessage}
      </div>
    );
  }

  const toolbar = (
    <PersonaScriptsToolbar
      scriptCount={visibleScripts.length}
      briefCount={visibleBriefs.length}
      angleCount={visibleGroups.length}
      allExpanded={allGroupsExpanded}
      onToggleExpandAll={handleToggleExpandAll}
      search={search}
      onSearchChange={handleSearchChange}
      filters={filters}
      sentStatusReady={isSentMapReady}
      onFilterChange={handleFilterChange}
      allSelected={allVisibleSelected}
      someSelected={someVisibleSelected}
      onToggleSelectAll={handleToggleSelectAll}
      canAddScript={canGenerateScripts}
      shouldHideBriefs={shouldHideBriefs}
      onAddScript={() => {
        if (!detail || !activeTenantId) return;
        setSuggestOpen(true);
      }}
    />
  );

  const generateModal =
    detail && activeTenantId ? (
      <PersonaGenerateScriptModal
        open={suggestOpen || importOpen}
        onClose={() => {
          setSuggestOpen(false);
          onImportOpenChange?.(false);
        }}
        tenantId={activeTenantId}
        mode="custom"
        setId={detail.set.id}
        setType={setType}
        initialTab={importOpen && !suggestOpen ? "import" : "idea"}
      />
    ) : null;

  if (totalItems === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden" data-testid="persona-scripts-tab">
        {toolbar}
        <div className={cn(PERSONA_STAGE_RESULTS_CLASS, "mb-4")} data-task-scroller="">
          <PersonaOpportunityTabPlaceholder
            icon="file-text"
            titleKey="personaOpportunities.tabs.scriptsPlaceholderTitle"
            descriptionKey="personaOpportunities.tabs.scriptsPlaceholderDescription"
            actionLabelKey="personaOpportunities.tabs.goToOpportunities"
            onAction={onGoToOpportunities}
          />
        </div>
        {generateModal}
      </div>
    );
  }

  const aiEditPrimary = aiEditScripts[0];
  const aiEditBriefPrimary = aiEditBriefsList[0];
  const selectedScripts = visibleScripts.filter((script) => selectedScriptIds.has(script.id));
  const selectedVisibleBriefs = visibleBriefs.filter((brief) => selectedBriefIds.has(brief.id));

  async function handleDeleteSelected() {
    if (!detail || !activeTenantId) return;
    const scriptIds = selectedScripts.map((script) => script.id);
    const briefIds = selectedVisibleBriefs.map((brief) => brief.id);
    if (scriptIds.length === 0 && briefIds.length === 0) return;

    const jobs: Promise<DeleteJobResult>[] = [];
    if (scriptIds.length > 0) {
      jobs.push(
        deleteScripts
          .mutateAsync({
            setId: detail.set.id,
            tenantId: activeTenantId,
            scriptIds,
          })
          .then((result) => ({ kind: "scripts" as const, deletedIds: result.deletedIds })),
      );
    }
    if (briefIds.length > 0) {
      jobs.push(
        deleteBriefs
          .mutateAsync({
            setId: detail.set.id,
            tenantId: activeTenantId,
            briefIds,
          })
          .then((result) => ({ kind: "briefs" as const, deletedIds: result.deletedIds })),
      );
    }

    const settled = await Promise.allSettled(jobs);
    for (const result of settled) {
      if (result.status === "rejected") continue;
      if (result.value.kind === "scripts") {
        setSelectedScriptIds((current) =>
          removeDeletedIds(current, result.value.deletedIds),
        );
      } else {
        setSelectedBriefIds((current) =>
          removeDeletedIds(current, result.value.deletedIds),
        );
      }
    }
    setDeleteOpen(false);
  }

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="persona-scripts-tab"
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {revealAnnouncement}
      </span>
      {toolbar}
      {hasQueryError ? (
        <div
          role="alert"
          className="mx-4 mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm text-destructive"
        >
          {loadErrorMessage}
        </div>
      ) : null}

      <div className={cn("mb-4 px-4 pt-4 pb-6", PERSONA_STAGE_RESULTS_CLASS)} data-task-scroller="">
        {visibleGroups.length === 0 ? (
          <PersonaOpportunityTabPlaceholder
            icon="search"
            titleKey="personaOpportunities.scripts.noMatchesTitle"
            titleFallback="No matching scripts"
            descriptionKey="personaOpportunities.scripts.noMatchesDescription"
            descriptionFallback="Try a different search or adjust filters."
          />
        ) : (
          <ul className="flex flex-col gap-3">
          {visibleGroups.map((group) => {
            const groupExpanded = expandedGroups[group.opportunityId] ?? true;
            const customCreators = group.isCustom ? uniqueScriptCreators(group.scripts) : [];
            const customCreatedBy = group.isCustom
              ? customGroupCreatedByLabel(customCreators, t)
              : null;
            return (
              <li
                key={group.opportunityId}
                data-testid={group.isCustom ? "persona-scripts-custom-group" : undefined}
                className={cn(
                  "overflow-hidden rounded-md border border-border bg-card",
                  group.isCustom && "border-l-4 border-l-primary",
                )}
              >
                <button
                  type="button"
                  aria-expanded={groupExpanded}
                  onClick={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [group.opportunityId]: !groupExpanded,
                    }))
                  }
                  className={cn(
                    "flex w-full cursor-pointer items-start gap-3 px-4 py-3.5 text-left transition-colors duration-150",
                    group.isCustom
                      ? "bg-primary/5 hover:bg-primary/10"
                      : "bg-muted/30 hover:bg-muted/50",
                  )}
                >
                  <Icon
                    name="chevron-right"
                    size="sm"
                    className={cn(
                      "mt-0.5 shrink-0 text-muted-foreground transition-transform duration-150 ease-out motion-reduce:transition-none",
                      groupExpanded && "rotate-90",
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-semibold leading-snug text-foreground">
                      {group.isCustom ? (
                        customCreators.length > 0 ? (
                          <span className="flex shrink-0 items-center" aria-hidden>
                            {customCreators.slice(0, 3).map((creator, index) => (
                              <UserAvatar
                                key={`${creator.displayName}-${index}`}
                                name={creator.displayName}
                                src={creator.photoUrl}
                                size="sm"
                                className={cn(
                                  "ring-2 ring-primary/5",
                                  index > 0 && "-ml-1.5",
                                )}
                              />
                            ))}
                          </span>
                        ) : (
                          <Icon name="user" size="sm" color="primary" className="shrink-0" aria-hidden />
                        )
                      ) : null}
                      {group.isCustom ? (
                        <HighlightText
                          text={t(
                            "personaOpportunities.scripts.customGroupTitle",
                            "Custom Scripts",
                          )}
                          query={listSearch}
                        />
                      ) : (
                        <HighlightText text={group.angle} query={listSearch} />
                      )}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      <HighlightText
                        text={
                          group.isCustom
                            ? (customCreatedBy ??
                              t(
                                "personaOpportunities.scripts.customGroupMeta",
                                "Imported and added on this set",
                              ))
                            : group.drivenFeature
                              ? t("personaOpportunities.scripts.groupMetaDriven", {
                                  defaultValue: "{{persona}} · {{driven}}",
                                  persona: group.personaTitle,
                                  driven: group.drivenFeature,
                                })
                              : group.personaTitle
                        }
                        query={listSearch}
                      />
                    </p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full border border-border px-2.5 py-0.5 text-[11px] font-medium",
                      group.isCustom
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-foreground",
                    )}
                  >
                    {groupCountLabel(group, t)}
                  </span>
                </button>

                <CollapsibleReveal open={groupExpanded}>
                  <ul className="flex flex-col gap-2.5 border-t border-border p-3 sm:p-4">
                    {group.scripts.map((script) => {
                      const scriptExpanded = expandedScripts[script.id] ?? false;
                      const isSelected = selectedScriptIds.has(script.id);
                      const isFavorite = script.isFavorite;
                      const untitled = t("personaOpportunities.scripts.untitledHook", {
                        defaultValue: "Script {{slot}}",
                        slot: script.slotIndex,
                      });
                      const hook = getPersonaScriptDisplayTitle(script, untitled);
                      const createdAtLabel = formatScriptCreatedAt(
                        script.createdAt,
                        i18n.language,
                      );
                      const variantMeta = getHookVariantDisplayMeta(
                        script,
                        listedScripts.filter(
                          (item) => item.personaOpportunityId === script.personaOpportunityId,
                        ),
                      );
                      const isHookVariant = isPersonaScriptHookVariant(script);
                      const strategyLabel = script.hookStrategy?.trim() || null;
                      const hookCount = script.hookCount ?? 1;
                      const originalBriefUrl = resolveOriginalBriefUrl(script);
                      const sentMeta = sentScriptsMap?.[script.id] ?? null;
                      return (
                        <li
                          key={script.id}
                          data-task-item={script.id}
                          tabIndex={-1}
                          className={cn(
                            "group/script scroll-mt-20 overflow-hidden rounded-md border bg-background outline-none",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border",
                            isHookVariant && "border-ai/50",
                          )}
                        >
                          <div
                            className="grid cursor-pointer grid-cols-[auto_auto_auto_minmax(0,1fr)_auto_auto] items-center gap-x-2 gap-y-1 px-3 py-3 sm:px-4 sm:py-3.5"
                            onClick={() => handleToggleScriptExpanded(script.id)}
                          >
                            <PersonaOpportunitySelectCheckbox
                              checked={isSelected}
                              onToggle={() => handleToggleSelected(script.id)}
                              label={t(
                                "personaOpportunities.scripts.selectScript",
                                "Select script",
                              )}
                            />
                            <Badge
                              variant="status-sent"
                              iconStart="file-text"
                              className="mt-px gap-[5px] px-2 py-0.5 text-[10.5px] font-medium tracking-[0.04em] uppercase [&>svg]:size-[11px]"
                            >
                              {t("personaOpportunities.scripts.badgeShort", "SCRIPT")}
                            </Badge>
                            <ScriptPlatformBadge format={script.youtubeFormat} />
                            <button
                              type="button"
                              aria-pressed={isFavorite}
                              aria-label={
                                isFavorite
                                  ? t(
                                      "personaOpportunities.scripts.unfavoriteAria",
                                      "Remove from favorites",
                                    )
                                  : t(
                                      "personaOpportunities.scripts.favoriteAria",
                                      "Add to favorites",
                                    )
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleFavorite(script);
                              }}
                              className={cn(
                                "inline-flex min-h-11 min-w-11 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
                                isFavorite
                                  ? "text-warning"
                                  : "text-muted-foreground hover:text-warning",
                              )}
                            >
                              <Icon
                                name="star"
                                size="sm"
                                color="inherit"
                                className={isFavorite ? "fill-warning text-warning" : undefined}
                                aria-hidden
                              />
                            </button>
                            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                              <PersonaScriptTitleEditor
                                title={hook}
                                canEdit={canEditScripts}
                                highlightQuery={listSearch}
                                isEditing={editingKey === `${script.id}:title`}
                                isFlashing={
                                  flash?.scriptId === script.id && flash.key === "title"
                                }
                                isSaving={savingKey === `${script.id}:title`}
                                onStartEdit={() => startScriptEditor(script.id, "title")}
                                onStopEdit={() =>
                                  setEditingKey((current) =>
                                    current === `${script.id}:title` ? null : current,
                                  )
                                }
                                onSave={(next) =>
                                  saveScriptContent(script.id, "title", {
                                    target: "hook",
                                    value: next,
                                  })
                                }
                              />
                              {hookCount > 1 ? (
                                <Badge variant="slate">
                                  {t("personaOpportunities.scripts.hookCountBadge", {
                                    defaultValue_one: "{{count}} hook",
                                    defaultValue_other: "{{count}} hooks",
                                    count: hookCount,
                                  })}
                                </Badge>
                              ) : null}
                              {isSentToAnyPartner(sentMeta) ? (
                                <Badge
                                  variant="status-sent"
                                  iconStart="check"
                                  className="border-info-subtle-foreground/30 px-2 py-1 text-[11px] font-semibold"
                                >
                                  {t(
                                    "personaOpportunities.scripts.sentToProductionBadge",
                                    "Sent to production",
                                  )}
                                </Badge>
                              ) : null}
                            </div>
                            {script.versionCount > 1 ? (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      aria-label={t(
                                        "personaOpportunities.scripts.history.aria",
                                        "Version history",
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setEditingKey(null);
                                        setAiEditKey(null);
                                        setHistoryScriptId(script.id);
                                      }}
                                      className="inline-flex h-[26px] min-h-11 items-center gap-[5px] rounded-sm border border-border bg-background px-2 text-[11px] tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-[26px]"
                                    >
                                      <Icon name="history" size="sm" color="inherit" aria-hidden />
                                      {script.versionCount}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom">
                                    {t(
                                      "personaOpportunities.scripts.history.aria",
                                      "Version history",
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span aria-hidden />
                            )}
                            <button
                              type="button"
                              aria-expanded={scriptExpanded}
                              aria-label={
                                scriptExpanded
                                  ? t("personaOpportunities.scripts.collapseScript", {
                                      defaultValue: "Collapse {{title}}",
                                      title: hook,
                                    })
                                  : t("personaOpportunities.scripts.expandScript", {
                                      defaultValue: "Expand {{title}}",
                                      title: hook,
                                    })
                              }
                              onClick={(event) => {
                                event.stopPropagation();
                                handleToggleScriptExpanded(script.id);
                              }}
                              className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
                            >
                              <Icon
                                name="chevron-right"
                                size="sm"
                                className={cn(
                                  "text-muted-foreground/70 transition-transform duration-150 ease-out motion-reduce:transition-none",
                                  scriptExpanded && "rotate-90",
                                )}
                                aria-hidden
                              />
                            </button>
                            <div className="col-start-4 flex min-w-0 flex-col gap-1">
                              {isHookVariant ? (
                                <Badge variant="ai" iconStart="sparkles" className="whitespace-normal text-left">
                                  <HighlightText
                                    text={
                                      strategyLabel
                                        ? t("personaOpportunities.scripts.hookVariantBadge", {
                                            defaultValue:
                                              "{{strategy}} · HOOK VARIANT OF S{{slot}}",
                                            strategy: strategyLabel,
                                            slot: variantMeta?.parentSlot ?? script.slotIndex,
                                          })
                                        : t(
                                            "personaOpportunities.scripts.hookVariantBadgeFallback",
                                            {
                                              defaultValue: "HOOK VARIANT OF S{{slot}}",
                                              slot:
                                                variantMeta?.parentSlot ?? script.slotIndex,
                                            },
                                          )
                                    }
                                    query={listSearch}
                                  />
                                </Badge>
                              ) : null}
                              {script.concept?.trim() ? (
                                <p className="text-xs italic text-muted-foreground">
                                  <HighlightText
                                    text={humanizeConceptLabel(script.concept.trim())}
                                    query={listSearch}
                                  />
                                </p>
                              ) : null}
                              <ScriptBroadcastSource source={script.broadcastSource} />
                              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] tabular-nums text-muted-foreground">
                                <span>{scriptMetaLine(script, t, variantMeta)}</span>
                                {createdAtLabel ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Icon name="clock" size="xs" color="muted" aria-hidden />
                                    {createdAtLabel}
                                  </span>
                                ) : null}
                              </p>
                              {isCustomAssetScript(script) ? (
                                <ScriptCustomAssetRowLink
                                  script={script}
                                  setId={detail.set.id}
                                  scope="persona-opportunities"
                                  className="mt-0.5"
                                />
                              ) : null}
                            </div>
                          </div>

                          <CollapsibleReveal open={scriptExpanded}>
                            <PersonaScriptExpandedBody
                              script={script}
                              setId={detail.set.id}
                              tenantId={activeTenantId}
                              canAddAlternateHooks={
                                !group.scripts.some((sibling) => sibling.parentScriptId === script.id)
                              }
                              searchQuery={listSearch}
                              isSelected={isSelected}
                              canEdit={canEditScripts}
                              originalBriefUrl={originalBriefUrl}
                              editingKey={
                                editingKey?.startsWith(`${script.id}:`) &&
                                editingKey !== `${script.id}:title`
                                  ? editingKey.slice(script.id.length + 1)
                                  : null
                              }
                              aiEditKey={
                                aiEditKey?.startsWith(`${script.id}:`)
                                  ? aiEditKey.slice(script.id.length + 1)
                                  : null
                              }
                              flashKey={
                                flash?.scriptId === script.id && flash.key !== "title"
                                  ? flash.key
                                  : null
                              }
                              flashSource={flash?.scriptId === script.id ? flash.source : "manual"}
                              savingKey={
                                savingKey?.startsWith(`${script.id}:`) &&
                                savingKey !== `${script.id}:title`
                                  ? savingKey.slice(script.id.length + 1)
                                  : null
                              }
                              aiBusyKey={
                                aiEdit.isPending &&
                                aiEdit.variables?.cell &&
                                aiEditKey?.startsWith(`${script.id}:`)
                                  ? aiEditKey.slice(script.id.length + 1)
                                  : null
                              }
                              onStartEdit={(key) => startScriptEditor(script.id, key)}
                              onStopEdit={() =>
                                setEditingKey((current) =>
                                  current?.startsWith(`${script.id}:`) &&
                                  current !== `${script.id}:title`
                                    ? null
                                    : current,
                                )
                              }
                              onStartAi={(key) => startScriptAiEditor(script.id, key)}
                              onStopAi={() =>
                                setAiEditKey((current) =>
                                  current?.startsWith(`${script.id}:`) ? null : current,
                                )
                              }
                              onApplyAi={(key, instruction) =>
                                applyCellAi(script.id, key, instruction)
                              }
                              onSaveCell={(sceneNumber, field, value) =>
                                saveScriptContent(script.id, `${sceneNumber}:${field}`, {
                                  target: "scene",
                                  sceneNumber,
                                  field,
                                  value,
                                })
                              }
                              hookCompare={
                                hookPanel?.parentScriptId === script.id
                                  ? {
                                      variants: hookPanel.variants,
                                      isConfirming: confirmHooks.isPending,
                                      onConfirm: (keptIndexes) => {
                                        if (!activeTenantId || !detail) return;
                                        confirmHooks.mutate(
                                          {
                                            setId: detail.set.id,
                                            scriptId: script.id,
                                            tenantId: activeTenantId,
                                            correlationId: hookPanel.correlationId,
                                            parentScriptId: script.id,
                                            keptIndexes,
                                          },
                                          { onSuccess: () => setHookPanel(null) },
                                        );
                                      },
                                      onDiscard: () => {
                                        discardedCorrelationIds.current.add(hookPanel.correlationId);
                                        setHookPanel(null);
                                      },
                                    }
                                  : null
                              }
                              showAddThreeHooks={
                                hookPanel?.parentScriptId !== script.id &&
                                canShowAddThreeHooks({
                                  canGenerateScripts: canEditScripts,
                                  script,
                                  siblings: group.scripts,
                                  isSent: script.isHookSetLocked === true,
                                })
                              }
                              isSuggesting={
                                suggestHooks.isPending &&
                                suggestHooks.variables?.scriptId === script.id
                              }
                              onAddThreeHooks={() => {
                                if (!activeTenantId) return;
                                suggestHooks.mutate({
                                  setId: detail.set.id,
                                  scriptId: script.id,
                                  tenantId: activeTenantId,
                                });
                              }}
                              onAiEdit={() => {
                                setEditingKey(null);
                                setAiEditKey(null);
                                setAiEditBriefsList([]);
                                setAiEditScripts([script]);
                              }}
                              onSendToVendor={() => setSendScriptIds([script.id])}
                              isAiEditing={
                                aiEdit.isPending &&
                                !aiEdit.variables?.cell &&
                                (aiEdit.variables?.scriptIds.includes(script.id) ?? false)
                              }
                              sentMeta={sentMeta}
                              orderHref={
                                sentMeta
                                  ? getRevealHref({
                                      route: "/persona-opportunities",
                                      targetId: sentMeta.orderId,
                                      search: {
                                        setId: detail.set.id,
                                        tab: "sent_to_vendor",
                                        orderId: sentMeta.orderId,
                                      },
                                    })
                                  : undefined
                              }
                              onOpenOrder={openOrder}
                            />
                          </CollapsibleReveal>
                        </li>
                      );
                    })}
                    {group.briefs.map((brief) => (
                      <li key={brief.id} data-task-item={brief.id} className="scroll-mt-6">
                        <PersonaBriefCard
                          brief={brief}
                          canEdit={canEditScripts}
                          searchQuery={listSearch}
                          isSelected={selectedBriefIds.has(brief.id)}
                          onToggleSelected={() => handleToggleBriefSelected(brief.id)}
                          expanded={expandedBriefs[brief.id] ?? false}
                          onExpandedChange={(open) =>
                            setExpandedBriefs((prev) => ({ ...prev, [brief.id]: open }))
                          }
                          onSendToVendor={() => {
                            setSendScriptIds([]);
                            setSendBriefIds([brief.id]);
                          }}
                          sentMeta={sentScriptsMap?.[brief.id] ?? null}
                          orderHref={
                            sentScriptsMap?.[brief.id]
                              ? getRevealHref({
                                  route: "/persona-opportunities",
                                  targetId: sentScriptsMap[brief.id]!.orderId,
                                  search: {
                                    setId: detail.set.id,
                                    tab: "sent_to_vendor",
                                    orderId: sentScriptsMap[brief.id]!.orderId,
                                  },
                                })
                              : undefined
                          }
                          onOpenOrder={
                            sentScriptsMap?.[brief.id]
                              ? (event) => {
                                  const meta = sentScriptsMap[brief.id]!;
                                  openOrder(meta.orderId, meta.orderScriptId, event);
                                }
                              : undefined
                          }
                          onToggleFavorite={
                            canEditScripts
                              ? () => handleToggleBriefFavorite(brief)
                              : undefined
                          }
                          onOpenHistory={() => {
                            setAiEditKey(null);
                            setHistoryBriefId(brief.id);
                          }}
                          onAiEdit={
                            canEditScripts
                              ? () => {
                                  setEditingKey(null);
                                  setAiEditKey(null);
                                  setAiEditScripts([]);
                                  setAiEditBriefsList([brief]);
                                }
                              : undefined
                          }
                          isAiEditing={
                            aiEditBriefs.isPending &&
                            !aiEditBriefs.variables?.cell &&
                            (aiEditBriefs.variables?.briefIds.includes(brief.id) ?? false)
                          }
                          onApplyAi={
                            canEditScripts
                              ? (cellKey, instruction) =>
                                  applyBriefCellAi(brief.id, cellKey, instruction)
                              : undefined
                          }
                          aiEditKey={
                            aiEditKey?.startsWith(`${brief.id}:`)
                              ? aiEditKey.slice(brief.id.length + 1)
                              : null
                          }
                          aiBusyKey={
                            aiEditBriefs.isPending &&
                            aiEditBriefs.variables?.cell &&
                            aiEditKey?.startsWith(`${brief.id}:`)
                              ? aiEditKey.slice(brief.id.length + 1)
                              : null
                          }
                          flashKey={
                            flash?.scriptId === brief.id && flash.key !== "title"
                              ? flash.key
                              : null
                          }
                          onStartAi={(key) => startBriefAiEditor(brief.id, key)}
                          onStopAi={() =>
                            setAiEditKey((current) =>
                              current?.startsWith(`${brief.id}:`) ? null : current,
                            )
                          }
                          onPatch={
                            detail && activeTenantId
                              ? async (patch) => {
                                  await patchBriefContent.mutateAsync({
                                    setId: detail.set.id,
                                    briefId: brief.id,
                                    tenantId: activeTenantId,
                                    patch,
                                  });
                                }
                              : undefined
                          }
                        />
                      </li>
                    ))}
                  </ul>
                </CollapsibleReveal>
              </li>
            );
          })}
          </ul>
        )}
      </div>

      <PersonaScriptBulkActionBar
        selectedScriptCount={selectedScripts.length}
        selectedBriefCount={selectedVisibleBriefs.length}
        onClearSelection={() => {
          setSelectedScriptIds(new Set());
          setSelectedBriefIds(new Set());
        }}
        onExport={handleExportSelected}
        isExporting={isExporting}
        onAiEdit={() => {
          setEditingKey(null);
          setAiEditKey(null);
          setAiEditBriefsList([]);
          setAiEditScripts(selectedScripts);
        }}
        onAiEditBriefs={() => {
          setEditingKey(null);
          setAiEditKey(null);
          setAiEditScripts([]);
          setAiEditBriefsList(selectedVisibleBriefs);
        }}
        onSendToVendor={() => {
          setSendScriptIds(selectedScripts.map((script) => script.id));
          setSendBriefIds(selectedVisibleBriefs.map((brief) => brief.id));
        }}
        onBroadcast={
          canBroadcast
            ? () =>
                setBroadcastScripts(
                  selectedScripts.map((script) => ({
                    id: script.id,
                    title:
                      scriptTitlesById[script.id] ??
                      t("personaOpportunities.scripts.untitledHook", {
                        defaultValue: "Script {{slot}}",
                        slot: script.slotIndex,
                      }),
                    descriptionLength: estimateBroadcastDescriptionLength(script),
                  })),
                )
            : undefined
        }
        onDelete={() => setDeleteOpen(true)}
        canAiEdit={canEditScripts}
        canDelete={canEditScripts}
        isAiEditing={
          (aiEdit.isPending && !aiEdit.variables?.cell) ||
          (aiEditBriefs.isPending && !aiEditBriefs.variables?.cell)
        }
        isDeleting={deleteScripts.isPending || deleteBriefs.isPending}
      />

      {aiEditPrimary && activeTenantId && detail ? (
        <PersonaAiScriptEditorModal
          open
          hook={aiEditPrimary.hook}
          concept={aiEditPrimary.concept}
          opportunityTitle={
            opportunityMetaById.get(aiEditPrimary.personaOpportunityId)?.angle ?? null
          }
          isSubmitting={aiEdit.isPending}
          onClose={() => setAiEditScripts([])}
          onSubmit={(instruction) => {
            aiEdit.mutate(
              {
                setId: detail.set.id,
                scriptIds: aiEditScripts.map((script) => script.id),
                instruction,
                tenantId: activeTenantId,
              },
              { onSuccess: () => setAiEditScripts([]) },
            );
          }}
        />
      ) : null}
      {aiEditBriefPrimary && activeTenantId && detail ? (
        <PersonaAiBriefEditorModal
          open
          videoName={aiEditBriefPrimary.videoName}
          opportunityTitle={
            opportunityMetaById.get(aiEditBriefPrimary.personaOpportunityId)?.angle ?? null
          }
          count={aiEditBriefsList.length}
          isSubmitting={aiEditBriefs.isPending}
          onClose={() => setAiEditBriefsList([])}
          onSubmit={(instruction, idempotencyKey) => {
            aiEditBriefs.mutate(
              {
                setId: detail.set.id,
                briefIds: aiEditBriefsList.map((brief) => brief.id),
                instruction,
                tenantId: activeTenantId,
                idempotencyKey,
              },
              { onSuccess: () => setAiEditBriefsList([]) },
            );
          }}
        />
      ) : null}
      {(sendScriptIds.length > 0 || sendBriefIds.length > 0) && activeTenantId ? (
        <PersonaSendToVendorDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setSendScriptIds([]);
              setSendBriefIds([]);
            }
          }}
          scriptIds={sendScriptIds}
          briefIds={sendBriefIds}
          scriptTitlesById={scriptTitlesById}
          scriptHooksById={scriptHooksById}
          scriptDetailsById={scriptDetailsById}
          briefDetailsById={briefDetailsById}
          briefTitlesById={briefTitlesById}
          sentScriptsMap={sentScriptsMap ?? {}}
          tenantId={activeTenantId}
          opportunitySetId={detail.set.id}
        />
      ) : null}
      {broadcastScripts.length > 0 ? (
        <BroadcastScriptsModal
          open
          onOpenChange={(open) => {
            if (!open) setBroadcastScripts([]);
          }}
          scripts={broadcastScripts}
          sourceSetName={detail?.set.name}
          onStarted={() => {
            setSelectedScriptIds(new Set());
            setSelectedBriefIds(new Set());
          }}
        />
      ) : null}
      <DeletePersonaScriptsDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        selectedScriptCount={selectedScripts.length}
        selectedBriefCount={selectedVisibleBriefs.length}
        isDeleting={deleteScripts.isPending || deleteBriefs.isPending}
        onConfirm={() => void handleDeleteSelected()}
      />
      {historyScript && activeTenantId && detail ? (
        <ScriptsHistoryDrawer
          open
          onOpenChange={(next) => {
            if (!next) setHistoryScriptId(null);
          }}
          setId={detail.set.id}
          tenantId={activeTenantId}
          script={historyScript}
          canRestore={canEditScripts}
        />
      ) : null}
      {historyBrief && activeTenantId && detail ? (
        <BriefsHistoryDrawer
          open
          onOpenChange={(next) => {
            if (!next) setHistoryBriefId(null);
          }}
          setId={detail.set.id}
          tenantId={activeTenantId}
          brief={historyBrief}
          canRestore={canEditScripts}
        />
      ) : null}
      {generateModal}
    </div>
  );
}

function CollapsibleReveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div
        className={cn(
          "min-h-0 overflow-hidden transition-opacity duration-150 ease-out motion-reduce:transition-none",
          open ? "opacity-100" : "opacity-0",
        )}
        inert={!open}
      >
        {children}
      </div>
    </div>
  );
}
